import { createFileRoute } from "@tanstack/react-router";
import Avatar from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import useAppStore from "@/store";
import type { Shop } from "@/store/Shop";
import { Check, Plus } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslate } from "@/hooks/use-translate";

export const Route = createFileRoute("/(onboarding)/setup/select-shop")({
  component: AccountSetupPage,
});

function AccountSetupPage() {
  const store = useAppStore();
  const navigate = useNavigate();
  const [selectedShop, setSelectedShop] = useState<Shop | undefined>(undefined);
  const { t } = useTranslate();

  const handleShopSelect = (shop: Shop) => {
    setSelectedShop(shop);
  };

  const handleContinue = () => {
    if (selectedShop) {
      store.loading.start("shop");
      store.user
        .updateCurrentShop(selectedShop?.id)
        .then(() => {
          navigate({ to: `/dashboard` });
        })
        .finally(() => {
          store.loading.stop("shop");
        });
    } else {
      store.errors.set("shop", t("No shop selected"));
    }
  };

  const user = store.auth.session?.user;

  useEffect(() => {
    store.auth.fetchSession().then((session) => {
      store.shop.getUserShops(session.user.id);
    });
  }, []);
  return (
    <>
      <div className="border-b border-gray-200 flex md:flex-row items-center justify-between z-20 p-4 gap-4 md:pl-8">
        <div className="flex gap-4">
          <img
            src="/assets/images/logo.svg"
            alt="SalesBox Logo"
            width={120}
            height={40}
          />
        </div>

        <div className="flex items-center gap-4 w-full md:w-auto justify-end">
          {user?.role != "shop_owner" && (
            <Link
              to="/setup/create-business"
              className="hover:underline text-sm hover:text-primary-600"
            >
              {t("Add a business")}
            </Link>
          )}

          <Link
            to="/auth/logout"
            className="hover:underline text-sm hover:text-primary-600"
          >
            {t("Logout")}
          </Link>
          <div
            className="flex cursor-pointer items-center gap-2"
            onClick={() => {
              store.display.show("SelectProfileModal");
            }}
          >
            <Avatar name={user?.first_name as string} url={undefined} />

            <div className="hidden sm:block">
              <h2 className="text-sm">{`${user?.first_name} ${user?.last_name}`}</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="md:h-14"></div>
      <div className="max-w-3xl mx-auto p-4 md:px-0 ">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("Select a Shop")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Choose which shop you want to manage")}
          </p>
          <div>{store.errors.render()}</div>
        </div>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
          {store.shop.items.map((shop) => (
            <div
              key={shop.id}
              className={`cursor-pointer transition-colors rounded-md ${selectedShop?.id === shop.id
                ? "border-2 border-secondary-500"
                : "border-gray-200 border-2 hover:border-secondary-200"
                }`}
              onClick={() => handleShopSelect(shop)}
            >
              <div className="p-4">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <img
                      src={shop.logo?.url || "/assets/images/shop.png"}
                      alt={`${shop.name} logo`}
                      width={120}
                      height={120}
                      className="rounded-full"
                    />
                    {selectedShop?.id === shop.id && (
                      <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-white">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <h3 className="font-medium truncate">{shop.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {shop.business.name}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {user?.role == "shop_owner" && (
            <div
              onClick={() => {
                navigate({ to: "/setup/create-shop" });
              }}
              className="cursor-pointer border-dashed border-2 rounded-md border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
            >
              <div className="p-4 flex flex-col items-center justify-center h-[100px]">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 mb-2">
                  <Plus className="h-5 w-5" />
                </div>
                <p className="font-medium text-sm">{t("Add New Shop")}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-center">
          <Button
            className="w-full max-w-md  text-white"
            disabled={store.loading.status.shop}
            loading={store.loading.status.shop}
            onClick={handleContinue}
          >
            {t("Continue")}
          </Button>
        </div>
      </div>
    </>
  );
}
